import { ItemStatusSliceType } from "../../../../services/redux/slices/itemStatusSlice";
import {
  OrderTypesKeysEnum,
  ReceiptSourceEnum,
  ReceiptSourceTypeEnum,
} from "../../../../utils/enums";
import {
  checkSubItemDetails,
  reCheckItemDetails,
} from "../../../../utils/help/itemDetails/itemDetails";
import {
  ItemType,
  PaidItem,
  PaidReceipt,
  ReceiptSubItemAddon,
  SubItemType,
} from "../../../../utils/types";

const lodashClonedeep = require("lodash.clonedeep");

/**
 * ReceiptSourceEnum mapping to display label translation keys.
 * WEB -> Web, MOBILE/PARTNER -> App, SELF_SERVICE/MOB_SELF_SERVICE/VISITOR_SELF_SERVICE/POS -> In-Store, (none) -> Call Center
 */
const RECEIPT_SOURCE_TO_LABEL_KEY: Record<ReceiptSourceEnum, string> = {
  [ReceiptSourceEnum.WEB]: "tip:online",
  [ReceiptSourceEnum.MOBILE]: "common:receipt-source-app",
  [ReceiptSourceEnum.PARTNER]: "common:receipt-source-app",
  [ReceiptSourceEnum.SELF_SERVICE]: "common:receipt-source-in-store",
  [ReceiptSourceEnum.MOB_SELF_SERVICE]: "common:receipt-source-in-store",
  [ReceiptSourceEnum.VISITOR_SELF_SERVICE]: "common:receipt-source-in-store",
  [ReceiptSourceEnum.POS]: "common:receipt-source-in-store",
};

/**
 * Returns the translation key for displaying receipt source label (e.g. Web, App, In-Store, Call Center).
 */
export const getReceiptSourceTranslationKey = (order: PaidReceipt): string => {
  const sourceType = order.source_type ?? order.receiptExtras?.source_type;

  if (sourceType === ReceiptSourceTypeEnum.CALL_CENTER) {
    return "common:call-center";
  }

  const source = order.source;
  if (!source) {
    return "common:call-center";
  }

  return RECEIPT_SOURCE_TO_LABEL_KEY[source] ?? "common:call-center";
};

/**
 * Returns the translation key for displaying order type label (e.g. Dine-in, Pick up, Delivery).
 */
export const getOrderTypeTranslationKey = (order: PaidReceipt): string => {
  const { orderType, deliveryReceipt } = order;

  if (
    orderType === OrderTypesKeysEnum.DELIVERYV2 &&
    deliveryReceipt?.franchiseDeliveryConfig?.deliveryType?.displayName
  ) {
    return `table-page:${deliveryReceipt.franchiseDeliveryConfig.deliveryType.displayName}`;
  }

  const keyMap: Record<OrderTypesKeysEnum, string> = {
    [OrderTypesKeysEnum.DINE_IN]: "table-page:table-dine-in",
    [OrderTypesKeysEnum.TAKE_OUT]: "table-page:table-take-out",
    [OrderTypesKeysEnum.DELIVERY]: "table-page:table-delivery",
    [OrderTypesKeysEnum.DELIVERYV2]: "table-page:table-delivery",
  };

  return keyMap[orderType] ?? "table-page:table-delivery";
};

export const checkIfItemAvailable = (
  itemStatusRedux: ItemStatusSliceType,
  receiptItem: PaidItem,
  hasRoundingMethodAndLevel: boolean = false
) => {
  receiptItem = lodashClonedeep(receiptItem);
  const statusItem = { ...receiptItem.subItem.item };
  statusItem.subItems = [{ ...receiptItem.subItem }];
  const itemStatus = reCheckItemDetails(
    itemStatusRedux,
    statusItem as ItemType,
    true,
    hasRoundingMethodAndLevel
  );
  const subItem = {
    ...itemStatus.item.subItems[0],
  } as SubItemType;
  const isOutOfStock = itemStatus.isOutOfStock;
  if (!isOutOfStock) {
    let addItemFlag = true;
    if (subItem.allAddonGroups) {
      const addonGroupData = handleAddonGroupsForItem(
        itemStatusRedux,
        subItem,
        hasRoundingMethodAndLevel
      );
      addItemFlag = addItemFlag && addonGroupData.addItemFlag;
      if (!addItemFlag) {
        return "addon-out-of-stock";
      } else {
        return "available";
      }
    }
  } else {
    return "out-of-stock";
  }
};

const getCountFromAddonList = (
  subItemId: number,
  addonList: ReceiptSubItemAddon[]
) => {
  return (
    addonList.find((addOn) => addOn.addonSubItem.subItem.id === subItemId)
      ?.count || 1
  );
};

/**
 * @param allAddonGroups
 * @param addonList
 * @returns
 */
export const validateAddonRequirements = (
  allAddonGroups: any[] | undefined,
  addonList: ReceiptSubItemAddon[]
): boolean => {
  if (!allAddonGroups || allAddonGroups.length === 0) {
    return true;
  }

  const selectedAddonsByGroup = new Map<number, ReceiptSubItemAddon[]>();
  addonList.forEach((addon) => {
    const groupId = addon.addonSubItem.addonGroupId;
    if (groupId) {
      if (!selectedAddonsByGroup.has(groupId)) {
        selectedAddonsByGroup.set(groupId, []);
      }
      selectedAddonsByGroup.get(groupId)!.push(addon);
    }
  });

  for (const addonGroup of allAddonGroups) {
    const minChoices = addonGroup.minChoices ?? 0;

    if (minChoices > 0) {
      const selectedAddons = selectedAddonsByGroup.get(addonGroup.id) || [];

      const totalSelections = selectedAddons.reduce(
        (sum, addon) => sum + (addon.count || 1),
        0
      );

      if (totalSelections < minChoices) {
        return false;
      }

      for (const selectedAddon of selectedAddons) {
        const matchingSubItem = addonGroup.addonGroupSubItem?.find(
          (subItem: any) =>
            subItem.subItem.id === selectedAddon.addonSubItem.subItem.id
        );

        if (matchingSubItem?.subItem?.allAddonGroups) {
          const nestedAddonsFromReceipt = selectedAddon.nestedAddons || [];

          const nestedAddonList: ReceiptSubItemAddon[] =
            nestedAddonsFromReceipt.map((nested: any) => ({
              id: nested.id,
              count: 1,
              addonSubItem: nested.nestedAddonSubItem,
              nestedAddons: [],
            }));

          if (
            !validateAddonRequirements(
              matchingSubItem.subItem.allAddonGroups,
              nestedAddonList
            )
          ) {
            return false;
          }
        }
      }
    }
  }

  return true;
};

export const handleAddonGroupsForItem = (
  itemStatusRedux: ItemStatusSliceType,
  subItem: SubItemType,
  hasRoundingMethodAndLevel: boolean,
  addonList?: ReceiptSubItemAddon[]
) => {
  let addItemFlag = true;
  let totalPrice = 0;
  if (!subItem.allAddonGroups) {
    return { addItemFlag, totalPrice };
  }
  for (const addonGroup of subItem.allAddonGroups) {
    if (!addItemFlag) {
      break;
    }
    for (const addonGroupSubItem of addonGroup.addonGroupSubItem) {
      if (!addItemFlag) {
        break;
      }
      const addonStatus = checkSubItemDetails(
        addonGroupSubItem.subItem,
        hasRoundingMethodAndLevel,
        itemStatusRedux
      );
      if (addonStatus.isOutOfStock) {
        addItemFlag = false;
        break;
      } else {
        const count = getCountFromAddonList(
          addonGroupSubItem.subItem.id!,
          addonList || []
        );
        totalPrice += addonGroupSubItem.price * count;
        addonGroupSubItem.count = count;
        if (
          addonGroupSubItem.subItem.item &&
          !addonGroupSubItem.subItem.item.subItems
        ) {
          addonGroupSubItem.subItem.item.subItems = [];
        }
        const nestedAddonGroupData = handleAddonGroupsForItem(
          itemStatusRedux,
          addonGroupSubItem.subItem,
          hasRoundingMethodAndLevel,
          addonList
        ) as any;
        if (!nestedAddonGroupData.addItemFlag) {
          addItemFlag = false;
          break;
        }
        totalPrice += nestedAddonGroupData.totalPrice;
        addItemFlag = addItemFlag && nestedAddonGroupData.addItemFlag;
      }
    }
  }

  return { addItemFlag, totalPrice };
};

// export const getAddonGroupSubItemIndex = (
//   allChosenAddonGroupSubItems: AddonGroupSubItemType[],
//   id: number
// ) => {
//   let removedIndex = -1;
//   for (
//     let chosenAddonGroupSubItemIndex = 0;
//     chosenAddonGroupSubItemIndex < allChosenAddonGroupSubItems.length;
//     chosenAddonGroupSubItemIndex++
//   ) {
//     if (allChosenAddonGroupSubItems[chosenAddonGroupSubItemIndex].id === id) {
//       removedIndex = chosenAddonGroupSubItemIndex;
//       break;
//     }
//   }

//   return removedIndex;
// };

// const handleAddonGroups = (subItem: SubItemType) => {
//   let addItemFlag = true;
//   let totalPrice = 0;
//   if (!subItem.allAddonGroups) {
//     return { addItemFlag, totalPrice };
//   }
//   for (const addonGroup of subItem.allAddonGroups) {
//     if (!addItemFlag) {
//       break;
//     }
//     const allChosenAddonGroupSubItems = [...addonGroup.addonGroupSubItem];
//     for (const addonGroupSubItem of addonGroup.addonGroupSubItem) {
//       if (!addItemFlag) {
//         break;
//       }
//       const addonStatus = checkSubItemDetails(addonGroupSubItem.subItem);
//       if (addonStatus.isOutOfStock) {
//         const removedIndex = getAddonGroupSubItemIndex(
//           allChosenAddonGroupSubItems,
//           addonGroupSubItem.id
//         );
//         if (removedIndex === -1) {
//           addItemFlag = false;
//           break;
//         }
//         allChosenAddonGroupSubItems.splice(removedIndex, 1);
//         continue;
//       }
//       totalPrice += addonGroupSubItem.price;
//       const nestedAddonGroupData = handleAddonGroups(
//         addonGroupSubItem.subItem
//       ) as any;
//       totalPrice += nestedAddonGroupData.totalPrice;
//       addItemFlag = addItemFlag && nestedAddonGroupData.addItemFlag;
//     }
//     if (allChosenAddonGroupSubItems.length < addonGroup.minChoices) {
//       addItemFlag = false;
//       break;
//     }
//     addonGroup.addonGroupSubItem = [...allChosenAddonGroupSubItems];
//   }

//   return { addItemFlag, totalPrice };
// };
